import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
    Trophy,
    Medal,
    Award,
    Mic,
    Briefcase,
    Search,
    Video,
    Flame,
    Crown,
    TrendingUp,
    Building2,
    UserCog,
    Clock,
    RefreshCw,
    Loader2,
    Star,
    Target
} from 'lucide-react';

const Leaderboard = () => {
    const { api, user, isAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('simulations');
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Filter states
    const [department, setDepartment] = useState('all');
    const [designation, setDesignation] = useState('all');
    const [timeRange, setTimeRange] = useState('all');

    const fetchFilters = useCallback(async () => {
        try {
            const response = await api.get('/leaderboard/filters');
            setFilters(response.data);
        } catch (error) {
            console.error('Failed to fetch filters:', error);
        }
    }, [api]);

    const fetchLeaderboard = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (department !== 'all') params.append('department', department);
            if (designation !== 'all') params.append('designation', designation);
            if (timeRange !== 'all') params.append('days', timeRange);
            params.append('limit', '50');
            
            let endpoint = '/leaderboard/simulations';
            switch (activeTab) {
                case 'job_simulations':
                    endpoint = '/leaderboard/job-simulations';
                    break;
                case 'sourcing_labs':
                    endpoint = '/leaderboard/sourcing-labs';
                    break;
                case 'evolve':
                    endpoint = '/leaderboard/evolve';
                    break;
                case 'streaks':
                    endpoint = '/leaderboard/streaks';
                    break;
                case 'overall':
                    endpoint = '/leaderboard/overall';
                    break;
                default:
                    endpoint = '/leaderboard/simulations';
            }
            
            const response = await api.get(`${endpoint}?${params}`);
            setLeaderboardData(response.data);
        } catch (error) {
            toast.error('Failed to load leaderboard');
        } finally {
            setLoading(false);
        }
    }, [api, activeTab, department, designation, timeRange]);

    useEffect(() => {
        fetchFilters();
    }, [fetchFilters]);

    useEffect(() => {
        fetchLeaderboard();
    }, [fetchLeaderboard]);

    const getRankIcon = (rank) => {
        switch (rank) {
            case 1: return <Crown className="h-6 w-6 text-yellow-500 fill-yellow-500" />;
            case 2: return <Medal className="h-6 w-6 text-slate-400" />;
            case 3: return <Award className="h-6 w-6 text-amber-700" />;
            default: return null;
        }
    };

    const getRankStyle = (rank) => {
        switch (rank) {
            case 1: return 'bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-transparent border-l-4 border-yellow-500';
            case 2: return 'bg-gradient-to-r from-slate-400/20 via-slate-300/10 to-transparent border-l-4 border-slate-400';
            case 3: return 'bg-gradient-to-r from-amber-600/20 via-amber-500/10 to-transparent border-l-4 border-amber-600';
            default: return 'hover:bg-muted/50';
        }
    };

    const featureConfig = {
        simulations: { icon: Mic, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Simulations' },
        job_simulations: { icon: Briefcase, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Job Simulations' },
        sourcing_labs: { icon: Search, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', label: 'Sourcing Labs' },
        evolve: { icon: Video, color: 'text-orange-500', bgColor: 'bg-orange-500/10', label: 'EVOLVE' },
        streaks: { icon: Flame, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'Streaks' },
        overall: { icon: Trophy, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', label: 'Overall' }
    };

    const currentConfig = featureConfig[activeTab] || featureConfig.simulations;
    const CurrentIcon = currentConfig.icon;

    return (
        <MainLayout>
            <div className="space-y-6" data-testid="leaderboard-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
                            <Trophy className="h-8 w-8 text-yellow-500" />
                            Leaderboard
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            See how you rank against your peers across all features
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <Card className="border-dashed">
                    <CardContent className="pt-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <Select value={timeRange} onValueChange={setTimeRange}>
                                    <SelectTrigger className="w-36" data-testid="filter-time-range">
                                        <SelectValue placeholder="Time Range" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Time</SelectItem>
                                        <SelectItem value="7">Last 7 Days</SelectItem>
                                        <SelectItem value="30">Last 30 Days</SelectItem>
                                        <SelectItem value="90">Last 90 Days</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <Select value={department} onValueChange={setDepartment}>
                                    <SelectTrigger className="w-40" data-testid="filter-department">
                                        <SelectValue placeholder="Department" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Departments</SelectItem>
                                        {filters?.departments?.map(dept => (
                                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <UserCog className="h-4 w-4 text-muted-foreground" />
                                <Select value={designation} onValueChange={setDesignation}>
                                    <SelectTrigger className="w-44" data-testid="filter-designation">
                                        <SelectValue placeholder="Designation" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Designations</SelectItem>
                                        {filters?.designations?.map(des => (
                                            <SelectItem key={des} value={des}>{des}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <Button variant="outline" size="icon" onClick={fetchLeaderboard} data-testid="refresh-btn">
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Feature Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-2 h-auto bg-transparent">
                        {Object.entries(featureConfig).map(([key, config]) => {
                            const Icon = config.icon;
                            return (
                                <TabsTrigger
                                    key={key}
                                    value={key}
                                    className={cn(
                                        "flex flex-col items-center gap-1 p-3 rounded-lg border data-[state=active]:border-primary data-[state=active]:bg-primary/5",
                                        "hover:bg-muted/50 transition-all"
                                    )}
                                    data-testid={`tab-${key}`}
                                >
                                    <Icon className={cn("h-5 w-5", config.color)} />
                                    <span className="text-xs font-medium">{config.label}</span>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {/* Leaderboard Content */}
                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Main Leaderboard */}
                        <Card className="lg:col-span-2">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={cn("p-2 rounded-lg", currentConfig.bgColor)}>
                                            <CurrentIcon className={cn("h-5 w-5", currentConfig.color)} />
                                        </div>
                                        <div>
                                            <CardTitle>{currentConfig.label} Leaderboard</CardTitle>
                                            <CardDescription>
                                                {leaderboardData?.total_participants || 0} participants
                                            </CardDescription>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                ) : leaderboardData?.leaderboard?.length > 0 ? (
                                    <div className="space-y-2">
                                        {leaderboardData.leaderboard.map((entry, idx) => {
                                            const isCurrentUser = entry.user_id === user?.id;
                                            return (
                                                <div
                                                    key={entry.user_id}
                                                    className={cn(
                                                        "flex items-center gap-4 p-3 rounded-lg transition-all",
                                                        getRankStyle(entry.rank),
                                                        isCurrentUser && "ring-2 ring-primary ring-offset-2"
                                                    )}
                                                    data-testid={`leaderboard-entry-${entry.rank}`}
                                                >
                                                    {/* Rank */}
                                                    <div className="w-12 flex items-center justify-center">
                                                        {getRankIcon(entry.rank) || (
                                                            <span className="text-lg font-bold text-muted-foreground">
                                                                #{entry.rank}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* User Info */}
                                                    <Avatar className="h-10 w-10">
                                                        <AvatarFallback className={cn(
                                                            "text-white font-semibold",
                                                            entry.rank === 1 ? "bg-gradient-to-br from-yellow-400 to-yellow-600" :
                                                            entry.rank === 2 ? "bg-gradient-to-br from-slate-400 to-slate-600" :
                                                            entry.rank === 3 ? "bg-gradient-to-br from-amber-500 to-amber-700" :
                                                            "bg-gradient-to-br from-primary to-secondary"
                                                        )}>
                                                            {entry.user_name?.charAt(0).toUpperCase() || '?'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold truncate">
                                                                {entry.user_name || 'Unknown'}
                                                            </p>
                                                            {isCurrentUser && (
                                                                <Badge variant="secondary" className="text-xs">You</Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                            {entry.department && (
                                                                <span className="flex items-center gap-1">
                                                                    <Building2 className="h-3 w-3" />
                                                                    {entry.department}
                                                                </span>
                                                            )}
                                                            {entry.designation && (
                                                                <span>• {entry.designation}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Stats */}
                                                    <div className="flex items-center gap-4 text-right">
                                                        <div>
                                                            <p className="text-xs text-muted-foreground">Sessions</p>
                                                            <p className="font-semibold">{entry.total_sessions || 0}</p>
                                                        </div>
                                                        {entry.avg_score !== undefined && (
                                                            <div>
                                                                <p className="text-xs text-muted-foreground">Avg Score</p>
                                                                <p className="font-bold text-lg">{entry.avg_score}</p>
                                                            </div>
                                                        )}
                                                        {entry.current_streak !== undefined && (
                                                            <div className="flex items-center gap-1">
                                                                <Flame className="h-4 w-4 text-orange-500" />
                                                                <span className="font-bold">{entry.current_streak}</span>
                                                            </div>
                                                        )}
                                                        {entry.overall_score !== undefined && (
                                                            <div>
                                                                <p className="text-xs text-muted-foreground">Overall</p>
                                                                <p className="font-bold text-lg">{entry.overall_score}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <CurrentIcon className={cn("h-12 w-12 mx-auto mb-4", currentConfig.color, "opacity-50")} />
                                        <p className="text-muted-foreground">No leaderboard data available yet</p>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Complete more sessions to appear on the leaderboard!
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Sidebar - Your Rank & Stats */}
                        <div className="space-y-6">
                            {/* Your Rank Card */}
                            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Star className="h-5 w-5 text-yellow-500" />
                                        Your Ranking
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {leaderboardData?.current_user?.rank ? (
                                        <div className="text-center">
                                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary mb-3">
                                                <span className="text-3xl font-bold text-white">
                                                    #{leaderboardData.current_user.rank}
                                                </span>
                                            </div>
                                            {leaderboardData.current_user.entry && (
                                                <div className="space-y-2 mt-4">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-muted-foreground">Sessions</span>
                                                        <span className="font-medium">
                                                            {leaderboardData.current_user.entry.total_sessions || 0}
                                                        </span>
                                                    </div>
                                                    {leaderboardData.current_user.entry.avg_score !== undefined && (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-muted-foreground">Avg Score</span>
                                                            <span className="font-medium">
                                                                {leaderboardData.current_user.entry.avg_score}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {leaderboardData.current_user.entry.best_score !== undefined && (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-muted-foreground">Best Score</span>
                                                            <Badge variant="secondary">
                                                                {leaderboardData.current_user.entry.best_score}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                                            <p className="text-sm text-muted-foreground">
                                                Complete sessions to get ranked!
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Top 3 Highlight */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Crown className="h-5 w-5 text-yellow-500" />
                                        Top 3
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {leaderboardData?.leaderboard?.slice(0, 3).length > 0 ? (
                                        <div className="space-y-3">
                                            {leaderboardData.leaderboard.slice(0, 3).map((entry) => (
                                                <div key={entry.user_id} className="flex items-center gap-3">
                                                    <div className="w-8 flex justify-center">
                                                        {getRankIcon(entry.rank)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium truncate text-sm">
                                                            {entry.user_name || 'Unknown'}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {entry.department || 'No dept'}
                                                        </p>
                                                    </div>
                                                    <Badge variant="outline">
                                                        {entry.avg_score || entry.overall_score || entry.current_streak || 0}
                                                    </Badge>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            No top performers yet
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Quick Stats */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                                        Quick Stats
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Total Participants</span>
                                        <span className="font-medium">
                                            {leaderboardData?.total_participants || 0}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Filter</span>
                                        <span className="font-medium">
                                            {timeRange === 'all' ? 'All Time' : `Last ${timeRange} days`}
                                        </span>
                                    </div>
                                    {department !== 'all' && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Department</span>
                                            <Badge variant="outline">{department}</Badge>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </Tabs>
            </div>
        </MainLayout>
    );
};

export default Leaderboard;
